import { Contact } from "planck-js";
import { Component } from "../core/Component";
import GameConfig from "../core/data/GameConfig";
import { GameObject } from "../core/GameObject";
import F84Game from "../Game";
import PlayerData from "../PlayerData";
import { GameScene } from "../scenes/GameScene";
import ActionQueue from "../utils/ActionQueue";
import { Coin } from "./Coin";
import { CoinBag } from "./CoinBag";
import { Crate } from "./Crate";
import { Dynamite } from "./Dynamite";
import { Firecracker } from "./Firecracker";
import { FirecrackerEffect } from "./FirecrackerEffect";
import { Ground } from "./Ground";
import { Hazard } from "./Hazard";
import { LightningParticleEmitter } from "./LightningParticleEmitter";
import { CharacterUnlockData } from "./MissionsState";
import { MovingObject } from "./MovingObject";
import { NPCRunner } from "./NPCRunner";
import { PlanckBodyComponent, PlanckBodyType, PlanckCollisonEvent } from "./PlanckBodyComponent";
import PlayerState, { PlayerStateTypes } from "./PlayerStates/PlayerState";
import PlayerStateBatarang from "./PlayerStates/PlayerStateBatarang";
import PlayerStateCrash from "./PlayerStates/PlayerStateCrash";
import PlayerStateFall from "./PlayerStates/PlayerStateFall";
import PlayerStateFirecracker from "./PlayerStates/PlayerStateFirecracker";
import PlayerStateFly from "./PlayerStates/PlayerStateFly";
import PlayerStateIdle from "./PlayerStates/PlayerStateIdle";
import PlayerStateLaunch from "./PlayerStates/PlayerStateLaunch";
import PlayerStateNegHit from "./PlayerStates/PlayerStateNegHit";
import PlayerStateRollerSkates from "./PlayerStates/PlayerStateRollerSkates";
import PlayerStateSmashFail from "./PlayerStates/PlayerStateSmashFail";
import PlayerStateSmashThroughWall from "./PlayerStates/PlayerStateSmashThroughWall";
import { Skates } from "./Skates";
import { SkatesEffect } from "./SkatesEffect";
import { Trampoline } from "./Trampoline";
import { WallBomb } from "./WallBomb";

export class Player extends GameObject {

	scene: GameScene;
	bgSpineContainer: SpineContainer;
	explosionSpine: SpineGameObject;
	spineContainer: SpineContainer;

	img: Phaser.GameObjects.Image;
	hitbox: Phaser.GameObjects.Image;
	currentState?: PlayerState;
	allStates: PlayerState[];
	planckBody: PlanckBodyComponent;
	slams = 1;
	maxSlams = 3;
	slamming = false;
	calledSlamTut = false;
	enableSlam = true;
	hitGroundThisFrame = false;
	stompedObjectThisFrame = false;
	stopped = false;
	static ON_STOPPED = "onStopped";
	static ON_COLLECT_COINS = "onCollectCoin";
	static ON_UPDATE_SLAM_METER = "onUpdateSlamMeter";
	static ON_SLAM_FAILED = "onSlamFailed";
	static ON_PASSED_WALL = "onPassedWall";
	static ON_HIT_GROUND = "onHitGround";
	static ON_FINISHED_FAIL_ANIM = "onFinishedFailAnim";
	static STOP_TUT_SLOW = "stopTutSlow";

	bounce = 0.7;
	frictionX = 0.6;
	frictionY = 0.6;
	maxSpeed: number;
	speedReductionFromStandardObjects: number;
	coinMultiplier = 1;
	detonatorMultiplier = 1;
	bounceSpeed = 0; // for debug - velocity the player bounced at
	hasLaunched: boolean;
	color: number;

	activeEffect?: Component;
	slamDelay: number;
	_gameScene: GameScene;
	touchingWall = false;
	character: CharacterUnlockData;
	private _springBounceMult: number;
	private _springBounceBonus: number;
	glow: SpineGameObject;
	slamIcon: Phaser.GameObjects.Image;

	constructor(scene: GameScene) {
		super(scene);
		this._gameScene = scene;
		this.maxSlams = PlayerData.getItemValue("anvil");
		this.slams = this.maxSlams;
		this.slamDelay = GameConfig.get<number>("slamDelay");
		if (!PlayerData.Instance.saveData.seenSlamTut) this.slamDelay = 5;
		this._springBounceMult = GameConfig.get("springPowerInverseMult");
		this._springBounceBonus = GameConfig.get("springPowerFlatBoost");
		const charIndex = PlayerData.Instance.saveData.currentCharacter;
		const character = this.character = GameConfig.get<CharacterUnlockData[]>("characterUnlocks")[charIndex];
		const rubberBand = PlayerData.Instance.saveData.items["rubberBand"];
		this.maxSpeed = GameConfig.get<number[]>("rubberBandMaxPlayerSpeed")[rubberBand];
		const grease = PlayerData.Instance.saveData.items["grease"];
		this.speedReductionFromStandardObjects = GameConfig.get<number[]>("greaseSpeedReductionFromStandardObjects")[grease];
		// const color = this.color = parseInt(character.color, 16);
		this.hasLaunched = false;
		this.bgSpineContainer = scene.add.spineContainer(0, 0);
		this.add(this.bgSpineContainer);

		const glow = this.glow = scene.add.spine(0, 0, "player-glow", "animation", true);
		this.bgSpineContainer.add(glow);
		glow.setAlpha(0);
		glow.setScale(0);
		glow.angle = -45;

		const img = this.img = this.addImage("whiteSquare");
		img.x += character.imageOffset.x;
		img.y += character.imageOffset.y;
		const hitbox = this.hitbox = this.addImage("whiteSquare");
		hitbox.alpha = 0;
		hitbox.setDisplaySize(96, hitbox.height);
		this.setSize(96, 130);
		this.planckBody = this.addComponent(new PlanckBodyComponent(this, PlanckBodyType.BOX, {
			x: 700, y: 300,
			width: hitbox.displayWidth, height: hitbox.height,
			worldScale: GameScene.worldScale,
			isDynamic: true,
			restitution: 1,
		}));
		this.planckBody.setFixedRotation(true);
		this.planckBody.fixture.setFriction(0);
		// this.planckBody.addCollisionListener(PlanckCollisonEvent.ON_PRE_SOLVE, (contact: Contact) => this.onPreSolve(contact));
		this.planckBody.addCollisionListener(PlanckCollisonEvent.ON_PRE_SOLVE, (contact: Contact) => this.onCollision(contact));

		const friction = PlayerData.getItemValue("grease");
		this.frictionX = friction.x;
		this.frictionY = friction.y;

		this.detonatorMultiplier = PlayerData.getItemValue("detonator");

		this.explosionSpine = this.scene.add.spine(0, 0, "explosion-generic");
		this.explosionSpine.setDisplaySize(this.explosionSpine.displayWidth / 3, this.explosionSpine.displayHeight / 3);
		this.spineContainer = this.scene.add.spineContainer(0, 0);
		this.spineContainer.add(this.explosionSpine);
		this.explosionSpine.visible = false;
		this.add(this.spineContainer);
		scene.alignIn.Center(this.spineContainer, img);

		// States
		const imgPrefix = character.name;
		const idleState = new PlayerStateIdle(this, imgPrefix);
		const smashFailState = new PlayerStateSmashFail(this, imgPrefix);
		const smashThroughWallState = new PlayerStateSmashThroughWall(this, imgPrefix);
		const fallState = new PlayerStateFall(this, imgPrefix);
		const launchState = new PlayerStateLaunch(this, imgPrefix);
		const flyState = new PlayerStateFly(this, imgPrefix);
		const crashState = new PlayerStateCrash(this, imgPrefix);
		const negHitState = new PlayerStateNegHit(this, imgPrefix);
		const batarangState = new PlayerStateBatarang(this, imgPrefix);
		const rollerSkateState = new PlayerStateRollerSkates(this, imgPrefix);
		const firecrackerState = new PlayerStateFirecracker(this, imgPrefix);

		this.allStates = [idleState, smashFailState, smashThroughWallState, fallState, launchState, flyState, crashState, negHitState, batarangState, rollerSkateState, firecrackerState];

		this.emit(PlayerStateTypes.Idle);

		this.scene.input.keyboard.on("keydown-G", () => {
			if (this.planckBody.isStatic()) this.planckBody.setDynamic();
			else this.planckBody.setStatic();
		});
	}

	update(dt: number): void {
		super.update(dt);
		if (this.currentState) this.currentState.update(dt);
		this.glow.angle = (Phaser.Math.Angle.Between(0, 0, this.planckBody.velocity.x, this.planckBody.velocity.y) * 180 / Math.PI) - 45;
		this.slamDelay -= dt;
		if (this.slamDelay <= 0) {
			this.slamDelay = 0;
			if (!this.touchingWall) this.enableSlam = true;
		}
		if (this.y < -4000 && this.planckBody.velocity.y < 0) {
			console.log("To high! setting vertical velocity back to 0")
			this.planckBody.velocity.y = 0;
		}
		if (!this.stopped) {
			if (this.planckBody.velocity.x <= 0 && this.hasLaunched) {
				this.planckBody.velocity.x = GameConfig.get("minXVelocity") as number;
			}
		}
		else if (this.stopped) {
			if (this.planckBody.velocity.x < 0) this.planckBody.velocity.x = 0;
			if (this.planckBody.velocity.x > 0) this.planckBody.velocity.x -= GameConfig.get<number>("slideFrictionOnStop") * dt;
		}

		if (this.planckBody.velocity.y > 0) this.bounceSpeed = 0;
		else {
			if (this.planckBody.velocity.y < this.bounceSpeed) this.bounceSpeed = this.planckBody.velocity.y;
		}

		const bbox = {
			minX: this.x - (this.hitbox.width / 2),
			maxX: this.x + (this.hitbox.width / 2),
			minY: this.y - (this.hitbox.height / 2),
			maxY: this.y + (this.hitbox.height / 2),
		};
		const coinBboxes = this.scene.ltGame.coinRtree.search(bbox);
		for (const bbox of coinBboxes) {
			bbox.obj.onCollect(this);
		}
	}

	physicsUpdate(dt: number): void {
		super.physicsUpdate(dt);

		const steepness = GameConfig.get<number>("gravitySteepness");
		const scaleMin = GameConfig.get<number>("gravityScaleMin");
		const scaleMax = GameConfig.get<number>("gravityScaleMax");
		const gravityScale = Phaser.Math.Clamp(Math.abs(this.planckBody.velocity.y * steepness) + scaleMin, 0, scaleMax);
		if (!this.stopped) this.planckBody.body.setGravityScale(gravityScale);

		if (this.planckBody.velocity.x > this.maxSpeed) this.planckBody.velocity.x -= GameConfig.get<number>("maxSpeedDecay") * dt;

		this.hitGroundThisFrame = false;
		this.stompedObjectThisFrame = false;
	}

	onCollision(contact: Contact): void {
		if (!this.scene) return;
		const fixtureA = contact.getFixtureA();
		const fixtureB = contact.getFixtureB();
		if (fixtureA != this.planckBody.fixture && fixtureB != this.planckBody.fixture) {
			return;
		}

		const objA = fixtureA.getBody().getUserData() as GameObject;
		const objB = fixtureB.getBody().getUserData() as GameObject;
		const them = (objA == this) ? objB : objA;
		const normal = contact.getManifold().localNormal;
		
		// don't let the physics system resolve collisions with anything but the ground
		if (!(them instanceof Ground)) contact.setEnabled(false);

		// trigger slam tutorial
		if (!(them instanceof CoinBag)) {
			if (!PlayerData.Instance.saveData.seenSlamTut) {
				if (!this.calledSlamTut) this.scene.tutSlow();
				this.calledSlamTut = true;
			}
			else this.emit(Player.STOP_TUT_SLOW);
		}

		const isGroundable = (them instanceof Dynamite || them instanceof Crate || them instanceof WallBomb || them instanceof Firecracker);
		const nonStompable = (them instanceof CoinBag) || (isGroundable && !them.grounded);
		const stompable = !nonStompable;

		// can only stomp one object per frame - exception for moving objects, where we can destroy multiple at a time.
		if (stompable) {

			const feet = this.y + this.height / 2;
			const objectTop = them.y - them.height / 2;
			const minStompVel = 2.5;
			// allow player to stomp objects if they have a minimum y-velocity and their feet aren't too far below the top of the object.
			const canStomp = (minVelocity: number, yThreshold: number) => {
				return this.planckBody.velocity.y >= minVelocity && (normal.y > 0 ||  feet - objectTop <= yThreshold);
				// return normal.y > 0 || (this.planckBody.velocity.y >= minVelocity && feet - objectTop <= yThreshold);
			};

			const isMovingObjectOrNPC = them instanceof MovingObject || them instanceof NPCRunner;
			const movingObjectCanStomp = canStomp(minStompVel, isMovingObjectOrNPC ? 60 : 100);
			// multi-stomp moving objects
			if (this.stompedObjectThisFrame && isMovingObjectOrNPC && (movingObjectCanStomp || normal.y > 0)) {
				them.playImpact(5 * this.coinMultiplier, this);
			}

			// block if player has already stomped one thing.
			if (this.stompedObjectThisFrame) return;

			let stomped = true; // set to false later if we don't end up stomping anything

			if (isMovingObjectOrNPC && movingObjectCanStomp) {
				them.playImpact(5 * this.coinMultiplier, this);

				if (this.slamming) this.slamHit();
	
				this.planckBody.velocity.x -= this.speedReductionFromStandardObjects;
				if (this.planckBody.velocity.x < 0){
					this.planckBody.velocity.x = GameConfig.get("minXVelocity") as number;
				} 
	
				if (this.planckBody.velocity.y > 0) this.planckBody.velocity.y *= -GameConfig.get<number>("movingObjectFrictionY");
				const minVY = GameConfig.get<number>("movingObjectBounceVelY");
				if (this.planckBody.velocity.y > minVY) this.planckBody.velocity.y = minVY;
				const maxVYBounce = GameConfig.get<number>("movingObjectMaxBounceVel");
				if (Math.abs(this.planckBody.velocity.y) > Math.abs(maxVYBounce)) this.planckBody.velocity.y = maxVYBounce;
	
				this.addSlam(GameConfig.get<number>("movingObjectBoostReward"));
				if (!this.usingAbility()) {
					this.evalFlight();
				}
				this.scene.soundManager.play("bounceObject");
				if (them instanceof NPCRunner) this.scene.soundManager.play("bounceCharacter");
			}
			else if ((them instanceof Hazard) && canStomp(minStompVel, 55)) {
				them.playImpact();
				const slamming = this.slamming;
				if (this.slamming) this.slamHit();
				if (this.planckBody.velocity.y > 0) this.planckBody.velocity.y *= -1;
				if (!slamming) {
					const slowdown = GameConfig.get<number>("hazardSlowdown");
					this.planckBody.velocity.x -= slowdown;
					this.planckBody.velocity.y -= slowdown;
					this.scene.ui.playHitGlow();
					this.scene.cameraController.shake();
					this.emit(PlayerStateTypes.NegHit, true);
				}
				if (!this.usingAbility()) {
					this.evalFlight();
				}
				this.scene.soundManager.play("hitYosemite");
			}
			else if ((them instanceof Dynamite || them instanceof Crate || them instanceof WallBomb) && canStomp(minStompVel, 60)) {
				this.hitExplosive(them);
			}
			else if (them instanceof Skates && canStomp(minStompVel, 90) && this.activeEffect == null) {
				them.playHitAnimation();
				this.addEffect(new SkatesEffect(this));
				this.scene.soundManager.play("useSkates");
			}
			else if (them instanceof Firecracker && canStomp(minStompVel, 60)) {
				this.hitFirecracker(them);
			}
			else if (them instanceof Trampoline && canStomp(minStompVel, 70)) {
				them.playBounceAnimation();
				if (this.slamming) this.slamHit();
				if (this.planckBody.velocity.y > 0) {
					this.planckBody.velocity.y *= -1;
					if (this.planckBody.velocity.y > -this._springBounceBonus) {
						this.planckBody.velocity.y = -this._springBounceBonus;
					}
				}
				this.planckBody.position.y = them.y - them.height / 2 - this.height / 2;
				this.addCoins(5 * this.coinMultiplier);
				this.scene.soundManager.play("collideSpring");
				this.emit(PlayerStateTypes.Fly, true);
			}
			else {
				// object was not stomped
				stomped = false;
			}

			if (stomped) this.stompedObjectThisFrame = true;
		}
		// non-stompable - can collide with these at any point.
		else {
			if (them instanceof CoinBag) {
				them.onCollect();
			}
				// flying firecracker
			else if (them instanceof Firecracker) {
				this.hitFirecracker(them);
			}
				// flying explosives
			else if ((them instanceof Dynamite || them instanceof Crate || them instanceof WallBomb)) {
				this.hitExplosive(them);
			}
		}

		if (them instanceof Ground) {
			const normal = contact.getManifold().localNormal;
			// ignore any collisions with the side of a ground piece
			if (Math.abs(normal.x) > 0.1) {
				contact.setEnabled(false);
				return;
			}

			if (!this.hitGroundThisFrame) {
				this.hitGroundThisFrame = true;
				const prevVX = this.planckBody.velocity.x;
				if (this.slamming || this.activeEffect) {
					this.slamHit();
					const volume = Phaser.Math.Clamp(this.planckBody.velocity.y / 10, 0, 1);
					this.scene.sound.play("bounceGround", { volume: volume });
					const slamVelMax = GameConfig.get<number>("slamVelocityYMax")
					if (Math.abs(this.planckBody.velocity.y) < slamVelMax) {
						this.planckBody.velocity.y *= -1;
					} else {
						this.planckBody.velocity.y = slamVelMax;
						//console.log("Slam velocity MAX" + this.planckBody.velocity.y);
					}
				}
				else {
					const volume = Phaser.Math.Clamp(this.planckBody.velocity.y / 10, 0, 1);
					this.scene.sound.play("bounceGround", { volume: volume });
					this.planckBody.velocity.x += this.frictionX;
					if (this.planckBody.velocity.x < 0) this.planckBody.velocity.x = 0;
					this.planckBody.velocity.y *= -this.frictionY;
					const maxVY = GameConfig.get<number>("groundMaxBounceVel");
					if (this.planckBody.velocity.y < maxVY) this.planckBody.velocity.y = maxVY;
					if (!this.usingAbility() && !this.stopped) {
						this.emit(PlayerStateTypes.NegHit);
					}
				}
				if ((Math.abs(this.planckBody.velocity.x) + Math.abs(this.planckBody.velocity.y)) < GameConfig.get<number>("magnToStop")) {
					this.planckBody.velocity.x = prevVX; // reset to velocity before ground slowed us down, so we can slide to a stop
					this.stop("ground");
				} else if (!this.slamming && this.activeEffect == undefined) this.scene.cameraController.shake();

				this.emit(Player.ON_HIT_GROUND);
			}
		}
	}

	addEffect(effect: Component) {
		this.addComponent(effect);
		this.activeEffect = effect;
	}

	removeEffect(): void {
		if (this.activeEffect) {
			this.removeComponent(this.activeEffect);
			this.activeEffect = null;
		}
	}

	hitExplosive(them: Dynamite | Crate | WallBomb): void {
		them.playHitAnimation();
		const key = (them instanceof Dynamite) ? "tnt" : "crate";
		const dx = (GameConfig.get(key + "VelocityBoostX") as number) * this.detonatorMultiplier;
		const mx = (GameConfig.get(key + "MaxVelocityX") as number);
		const dy = (GameConfig.get(key + "VelocityBoostY") as number);
		const my = (GameConfig.get(key + "MaxVelocityY") as number);
		const minx = (GameConfig.get(key + "MinVelBoostX") as number);
		if (this.planckBody.velocity.y > 0) this.planckBody.velocity.y *= -1;
		if (Math.abs(this.planckBody.velocity.y) < dy) this.planckBody.velocity.y = -dy
		else if (this.planckBody.velocity.y > -my) {
			this.planckBody.velocity.y -= dy;
			if (this.planckBody.velocity.y < -my) this.planckBody.velocity.y = -my;
		}
		if (this.planckBody.velocity.x < minx) this.planckBody.velocity.x = minx;
		else if (this.planckBody.velocity.x < mx) {
			this.planckBody.velocity.x += dx;
			if (this.planckBody.velocity.x > mx) this.planckBody.velocity.x = mx;
		}
		if (this.slamming) this.slamHit();
		this.addSlam(GameConfig.get<number>(key + "BoostReward"));
		this.addCoins(10 * this.coinMultiplier);
		this.scene.soundManager.play("collideTnt");

		this._gameScene.missionTracker.updateMissionProgress("triggerExplosives", 1);
		if (!this.usingAbility()) {
			this.evalFlight();
		}
		this.emit(PlayerStateTypes.Fly, true);
	}

	hitFirecracker(them: Firecracker): void {
		if (this.activeEffect == null) {
			this.addEffect(new FirecrackerEffect(this));
			them.playHitAnimation();
			this.emit(PlayerStateTypes.Firecracker);
			this.scene.soundManager.play("useFirecracker");
		}
	}

	collectCoin(): void {
		this.addCoins(1);
		this.addSlam(GameConfig.get<number>("coinBoostReward"));
	}

	addCoins(amt: number): void {
		this.emit(Player.ON_COLLECT_COINS, Math.round(amt));
	}

	addSlam(amt: number): void {
		const prevSlames = this.slams;
		this.slams += amt;
		this.slams = Phaser.Math.Clamp(this.slams, 0, this.maxSlams);
		if (Math.floor(prevSlames) < Math.floor(this.slams)) this.scene.soundManager.play("slamReady");
		this.emit(Player.ON_UPDATE_SLAM_METER, this.slams);
	}

	slam(): void {
		if (this.slams < 1) {
			this.scene.soundManager.play("noSlam");
			this.emit(Player.ON_SLAM_FAILED, this.slams);
			return;
		}
		if (!this.enableSlam || this.slamDelay > 0 || this.activeEffect) {
			return;
		}
		this.emit(Player.STOP_TUT_SLOW);
		if (!PlayerData.Instance.saveData.seenSlamTut) {
			PlayerData.Instance.saveData.seenSlamTut = true;
			PlayerData.Instance.save();
		}
		this.removeEffect();
		this.slamDelay = GameConfig.get<number>("slamDelay");
		this.planckBody.velocity.y = Math.max(this.planckBody.velocity.y, GameConfig.get<number>("slamVelocityY"));
		this.slamming = true;
		this.scene.soundManager.play("useSlam");
		// this.img.tint = 0xFFAA44;
		this.addSlam(-1);
		this.scene.ui.useSlam(this.slams);
		this.emit(PlayerStateTypes.Batarang);
	}

	createSlamIcon(): void {
		if (this.slamIcon) return;
		const icon = this.slamIcon = this.addImage("slamAlert");
		icon.setPosition(0, -225);
	}

	slamHit(): void {
		if (this.usingAbility()) return;
		const dx = (GameConfig.get("slamVelocityBoostX") as number);
		const mx = (GameConfig.get("slamMaxVelocityX") as number);
		if (this.planckBody.velocity.x < mx) {
			this.planckBody.velocity.x += dx;
			if (this.planckBody.velocity.x > mx) this.planckBody.velocity.x = mx;
		}
		this.scene.soundManager.play("slamBounce");
		this.slamming = false;
		this.playSlamHitAnim();
		this.scene.cameraController.shake();
		this.emit(PlayerStateTypes.Fly, true);
	}

	destroySlamIcon(): void {
		const icon = this.slamIcon;
		if (icon) {
			const glow = this.addImage("slamAlertGlow");
			this.addAt(glow, this.getIndex(this.slamIcon));
			glow.setPosition(icon.x, icon.y);

			const queue = new ActionQueue();
			queue.add(complete => {
				this.scene.tweens.add({
					targets: glow,
					scale: { from: 0, to: 1 },
					duration: 150,
					ease: Phaser.Math.Easing.Quadratic.Out,
					onComplete: complete,
				});
			});
			queue.add(complete => {
				this.scene.tweens.add({
					targets: glow,
					scale: { from: 0, to: 1.5 },
					alpha: { from: 1, to: 0 },
					duration: 150,
					ease: Phaser.Math.Easing.Linear,
					onComplete: () => glow.destroy(),
				});
			});
			queue.start();
			this.scene.tweens.add({
				targets: icon,
				scale: { from: 1, to: 1.5 },
				alpha: 0,
				duration: 300,
				ease: Phaser.Math.Easing.Linear,
				onComplete: () => icon.destroy(),
			});

			const emitter = new LightningParticleEmitter(this.scene);
			this.add(emitter);
			emitter.setPosition(icon.x, icon.y);
			this.scene.addObject(emitter, "none");
		}
		this.slamIcon = null;
	}

	playSlamHitAnim(): void {
		const character = this.character;
		let skinName = "batman-" + F84Game.Instance.locale;
		if (character.name.includes("Batman")){
			this.spawnSpine("player-smash-batman", "batarang-explode-comp", 0, 50, 0.7);
			skinName = "batman-" + F84Game.Instance.locale;
		} 
		if (character.name.includes("Daphne")){
			this.spawnSpine("player-smash-scooby", "animation", 0, 50, 0.7);
			skinName = "scooby-" + F84Game.Instance.locale;
			if (F84Game.Instance.locale != "en-us") skinName = "wonderwoman-" + F84Game.Instance.locale;
		} 
		if (character.name.includes("Flinstone") || character.name.includes("Flintstone")){
			this.spawnSpine("player-smash-flinstones", "animation", 0, 50, 0.7);
			skinName = "flintstones-" + F84Game.Instance.locale;
			if (F84Game.Instance.locale != "en-us") skinName = "wonderwoman-" + F84Game.Instance.locale;
		} 
		if (character.name.includes("Superman")){
			this.spawnSpine("player-smash-superman", "animation", 0, 50, 0.7);
			skinName = "superman-" + F84Game.Instance.locale;
		} 
		if (character.name.includes("Wonderwoman")){
			this.spawnSpine("player-smash-wonderwoman", "animation", 0, 50, 0.7);
			skinName = "wonderwoman-" + F84Game.Instance.locale;
		}
		const textSpine = this.scene.add.spine(0, -200, "player-smash-text", "text").setSkinByName(skinName);
		this.spineContainer.add(textSpine);
		textSpine.on("complete", () => textSpine.destroy());

	}

	spawnSpine(spineKey: string, anim: string, ox: number, oy: number, scale: number): void {
		const container = this.scene.add.spineContainer(this.x + ox, this.y + oy);
		this.scene.layers.get("player").add(container);
		const spine = this.scene.add.spine(0, 0, spineKey, anim);
		spine.scale = scale;
		container.add(spine);
		spine.on("complete", () => spine.destroy());
	}

	hitWallBit(minVelocity: number): boolean {
		if (this.stopped) {
			this.planckBody.velocity.x = 0;
			return;
		};
		const v = this.planckBody.velocity;
		let vel = Phaser.Math.Distance.Between(v.x, v.y, 0, 0);
		vel -= PlayerData.getItemValue("steelBall");

		if (v.x < minVelocity) {
			this.stop("inWall");
			this.planckBody.setGravityScale(0);
			return false;
		}
		else {
			this.planckBody.velocity.normalize();
			this.planckBody.velocity.mul(vel);
			if (this.currentState.type != PlayerStateTypes.SmashThroughWall && !this.usingAbility()) this.emit(PlayerStateTypes.SmashThroughWall);
			return true;
		}
	}

	testWallEnterExit(x: number, minVelocity: number, entering: boolean): boolean {
		if (this.planckBody.velocity.x < minVelocity) {
			this.x = x - this.width / 2;
			this.stop(entering ? "wallEnter" : "wallExit");
			this.planckBody.setGravityScale(0);
			this.emit(PlayerStateTypes.SmashFail);
			return false;
		}
		return true;
	}

	onEnteredWall(): void {
		this.scene.soundManager.play("breakWall");
		this.enableSlam = false;
		this.touchingWall = true;
		const anim = this.scene.add.spine(this.x, this.y, "wall-smash-breakin", "wall-success", false);
		anim.scale = 0.5;
		if(this.activeEffect && this.activeEffect instanceof SkatesEffect) {
			this.activeEffect.destroyEffect();
		}
		this.scene.layers.getSpineContainer("wall").add(anim);
		anim.once("complete", () => {
			anim.destroy();
		});
	}

	onPassedWall(): void {
		this.scene.soundManager.play("breakWall");
		this._gameScene.missionTracker.updateMissionProgress("breakWalls", 1);
		const envReached = Math.min(this.scene.ltGame.chunkSpawner.furthestEnvironmentReached, 5);
		this.scene.updateMusicTrack(envReached);
		this._gameScene.missionTracker.setMissionProgress("reachEnvironmentIndex", envReached);
		this.emit(Player.ON_PASSED_WALL);
		this.enableSlam = true;
		this.touchingWall = false;
		const anim = this.scene.add.spine(this.x, this.y, "wall-smash-breakout", "wall-breakout", false);
		anim.scale = 0.5;
		this.scene.layers.getSpineContainer("wall").add(anim);
		anim.once("complete", () => {
			anim.destroy();
		});
	}

	stop(type: "ground" | "wallEnter" | "wallExit" | "inWall"): void {
		if (this.stopped) return;
		this.stopped = true;
		this.planckBody.velocity.y = 0;
		this.emit(Player.ON_STOPPED);
		if (type == "ground") {
			this.emit(PlayerStateTypes.Crash);
			if (GameConfig.get("screenShakeOnStop")) this.scene.cameraController.shake(5);
			this.addCrashParticles("on-ground");
			this.scene.time.delayedCall(2000, () => this.emit(Player.ON_FINISHED_FAIL_ANIM));
		}
		if (type == "wallEnter" || type == "wallExit") {
			this.planckBody.velocity.x = 0;
			this.scene.cameraController.shake();
			this.addCrashParticles("on-ground");
			if (type == "wallEnter") this.slideAnim();
			else this.scene.time.delayedCall(2000, () => this.emit(Player.ON_FINISHED_FAIL_ANIM));
		}
		if (type == "inWall") {
			this.planckBody.velocity.x = 0;
			this.emit(PlayerStateTypes.Crash);
			this.addCrashParticles("in-wall");
			this.scene.time.delayedCall(2000, () => this.emit(Player.ON_FINISHED_FAIL_ANIM));
		}
	}

	addCrashParticles(animName: "on-ground" | "in-wall"): void {
		const anim = this.scene.add.spine(0, 0, "game-end-particles", animName, false);
		this.bgSpineContainer.add(anim);
		anim.once("complete", () => anim.destroy());
	}

	slideAnim(): void {
		this.removeComponent(this.getComponent(PlanckBodyComponent));
		const queue = new ActionQueue();
		queue.add(complete => this.scene.time.delayedCall(500, complete));
		queue.add(complete => {
			const targetY = (1080 + this.character.slideOffset) - this.scene.ltGame.chunkSpawner.groundHeight;
			const dist = targetY - this.y;
			this.scene.tweens.add({
				targets: this,
				y: targetY,
				delay: 500,
				duration: dist * (1500 / 1000),
				ease: Phaser.Math.Easing.Quadratic.In,
				onComplete: complete,
			});
		});
		queue.add(complete => {
			this.emit(PlayerStateTypes.Crash);
			this.scene.time.delayedCall(1000, complete);
		});
		queue.add(() => this.emit(Player.ON_FINISHED_FAIL_ANIM));
		queue.start();
	}

	/**
	 * Determine the current PlayerState based on velocity
	 */
	evalFlight(): void {
		if (this.stopped) return;
		if (this.currentState && this.currentState instanceof PlayerStateNegHit && this.currentState.spin) return;
		if (this.activeEffect != null) return;
		
		if (this.planckBody) {
			this.hasLaunched = true;
			if (this.planckBody.velocity.y <= 0) {
				// fly up
				if (Math.abs(this.planckBody.velocity.y) >= Math.abs(this.planckBody.velocity.x * 2)) {
					this.emit(PlayerStateTypes.Fly);
				} else {
					this.emit(PlayerStateTypes.Launch);
				}
			} else {
				// fall
				this.emit(PlayerStateTypes.Fall);
			}
		}
	}

	usingAbility(): boolean {
		if (this.currentState == null || (this.currentState.type != PlayerStateTypes.RollerSkates && this.currentState.type != PlayerStateTypes.Firecracker)) {
			return false;
		} else {
			return true;
		}
	}

	/**
	 * Plays generic explosion animation on player after riding the roller skates
	 */
	endEffect(): void {
		if (this.activeEffect != null) this.removeEffect();
		this.playExplosion();
		// this.evalFlight();
	}

	playExplosion(): void {
		this.explosionSpine.visible = true;
		this.explosionSpine.play("hit", false);
		this.explosionSpine.on("complete", () => {
			this.explosionSpine.visible = false;
		});
	}

	playWhoom(): void {
		const whoomSpine = this.scene.add.spine(-275, 150, "whoom", "animation", false);
		whoomSpine.setSkinByName("whoom-" + F84Game.Instance.locale);
		whoomSpine.scale = 0.5;
		this.spineContainer.add(whoomSpine);
		whoomSpine.once("complete", () => whoomSpine.destroy());

		this.scene.tweens.add({
			targets: this.glow,
			alpha: 1,
			scale: 1,
			duration: 300,
		})
	}

	createTutFinger(): void {
		const tutHand = this.addImage("tutHand")
		tutHand.setOrigin(10 / tutHand.width, 100 / tutHand.height);
		tutHand.angle = 45;
		const tween = this.scene.tweens.add({
			targets: tutHand,
			scale: 1.2,
			ease: Phaser.Math.Easing.Quadratic.Out,
			yoyo: true,
			duration: 500,
			loop: -1,
		});
		this.on(Player.STOP_TUT_SLOW, () => {
			tween.stop();
			tutHand.destroy();
		}, this);
	}
}